/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import forge from "node-forge";

import type {
  CryptoPrivateKey,
  CryptoPublicKey,
  JwtClaims,
  JwtHeader,
  TForgeCryptoKeyPair
} from "./TCryptoProvider";

export class ForgeCryptoProvider {
  private static readonly TAG: string = "ForgeCryptoProvider";

  /** Message thrown when a caller supplies a non-PEM private key. */
  private static readonly ERROR_EXPECTED_PEM: string =
    "expected PEM private key";

  private constructor() {}

  // ---------------------------------------------------------------------------
  // Key Management
  // ---------------------------------------------------------------------------

  /** Generates a 2048-bit RSA key pair in PEM format. */
  public static async generateRsaKeyPair(): Promise<TForgeCryptoKeyPair> {
    const keypair = forge.pki.rsa.generateKeyPair({
      bits: 2048,
      e: 0x10001 // 65537 equivalent to WebCryptoProvider Unit8Array([1, 0, 1])
    });

    return {
      publicKey: forge.pki.publicKeyToPem(keypair.publicKey),
      privateKey: forge.pki.privateKeyToPem(keypair.privateKey)
    };
  }

  /** Returns PEM public key after type validation. */
  public static async getPublicKeyPem(
    publicKey: CryptoPublicKey
  ): Promise<string> {
    if (typeof publicKey !== "string") {
      throw new Error(ForgeCryptoProvider.TAG + " expected PEM public key");
    }

    return publicKey;
  }

  /** Converts PEM public key to SPKI bytes. */
  public static async getPublicKeySpki(
    publicKeyPem: CryptoPublicKey
  ): Promise<ArrayBuffer> {
    if (typeof publicKeyPem !== "string") {
      throw new Error(ForgeCryptoProvider.TAG + " expected PEM public key");
    }

    const lines = publicKeyPem.split("\n");
    let base64 = "";

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      if (
        line.length > 0 &&
        line !== "-----BEGIN PUBLIC KEY-----" &&
        line !== "-----END PUBLIC KEY-----"
      ) {
        base64 += line;
      }
    }

    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    return bytes.buffer;
  }

  // ---------------------------------------------------------------------------
  // Hashing & Utility
  // ---------------------------------------------------------------------------

  /** Computes SHA-256 hash from JSON-serialized input. */
  public static async computeSha256(data: unknown): Promise<string> {
    const md = forge.md.sha256.create();

    md.update(JSON.stringify(data), "utf8");

    return md.digest().toHex();
  }

  /** Generates RFC4122 v4 UUID string. */
  public static generateUuidV4(): string {
    const bytes = forge.random.getBytesSync(16);
    const arr = new Uint8Array(16);

    for (let i = 0; i < 16; i++) {
      arr[i] = bytes.charCodeAt(i);
    }

    // RFC4122 v4
    arr[6] = (arr[6] & 0x0f) | 0x40;
    arr[8] = (arr[8] & 0x3f) | 0x80;

    const hex = Array.from(arr)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");

    return (
      hex.slice(0, 8) +
      "-" +
      hex.slice(8, 12) +
      "-" +
      hex.slice(12, 16) +
      "-" +
      hex.slice(16, 20) +
      "-" +
      hex.slice(20)
    );
  }

  // ---------------------------------------------------------------------------
  // Signing
  // ---------------------------------------------------------------------------

  /** Signs DLID and returns base64url signature. */
  public static async signDlid(
    dlid: string,
    privateKeyPem: CryptoPrivateKey
  ): Promise<string> {
    if (typeof privateKeyPem !== "string") {
      throw new Error(
        ForgeCryptoProvider.TAG + " " + ForgeCryptoProvider.ERROR_EXPECTED_PEM
      );
    }

    const privateKey = forge.pki.privateKeyFromPem(privateKeyPem);
    const md = forge.md.sha256.create();

    md.update(dlid, "utf8");

    const signature = privateKey.sign(md);

    return ForgeCryptoProvider.binaryToBase64Url(signature);
  }

  /** Signs JWT claims with RS256. */
  public static async signJwtRS256(
    claims: JwtClaims,
    privateKey: CryptoPrivateKey,
    header: JwtHeader
  ): Promise<string> {
    if (typeof privateKey !== "string") {
      throw new Error(
        ForgeCryptoProvider.TAG + " " + ForgeCryptoProvider.ERROR_EXPECTED_PEM
      );
    }

    return ForgeCryptoProvider.signJwt(header, claims, privateKey, () =>
      forge.md.sha256.create()
    );
  }

  /** Signs JWT claims with RS512. */
  public static async signJwtRS512(
    claims: JwtClaims,
    privateKey: CryptoPrivateKey,
    header: JwtHeader
  ): Promise<string> {
    if (typeof privateKey !== "string") {
      throw new Error(
        ForgeCryptoProvider.TAG + " " + ForgeCryptoProvider.ERROR_EXPECTED_PEM
      );
    }

    return ForgeCryptoProvider.signJwt(header, claims, privateKey, () =>
      forge.md.sha512.create()
    );
  }

  // ---------------------------------------------------------------------------
  // Private Helpers
  // ---------------------------------------------------------------------------

  /** Encodes text as base64url. */
  private static base64UrlEncode(value: string): string {
    let result = btoa(value).replace(/\+/g, "-").replace(/\//g, "_");

    while (result.endsWith("=")) {
      result = result.slice(0, -1);
    }

    return result;
  }

  /** Converts raw binary string to base64url. */
  private static binaryToBase64Url(binary: string): string {
    let result = btoa(binary).replace(/\+/g, "-").replace(/\//g, "_");

    while (result.endsWith("=")) {
      result = result.slice(0, -1);
    }

    return result;
  }

  /** Encodes JSON value as base64url. */
  private static encodeJson(value: unknown): string {
    return ForgeCryptoProvider.base64UrlEncode(JSON.stringify(value));
  }

  /** Builds and signs a JWT using provided digest factory. */
  private static signJwt(
    header: JwtHeader,
    claims: JwtClaims,
    privateKeyPem: string,
    mdFactory: () => forge.md.MessageDigest
  ): string {
    const encodedHeader = ForgeCryptoProvider.encodeJson(header);
    const encodedPayload = ForgeCryptoProvider.encodeJson(claims);
    const signingInput = encodedHeader + "." + encodedPayload;

    const privateKey = forge.pki.privateKeyFromPem(privateKeyPem);
    const md = mdFactory();

    md.update(signingInput, "utf8");

    const signature = privateKey.sign(md);
    const encodedSignature = ForgeCryptoProvider.base64UrlEncode(signature);

    return signingInput + "." + encodedSignature;
  }
}
